import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Cloud, Home, Check, HelpCircle, Briefcase } from 'lucide-react';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const ACCOUNTING_MODES = [
  {
    id: 'holded',
    title: 'Integración con Holded',
    description: 'Conecta tu cuenta existente de Holded para sincronizar facturas y gastos automáticamente.',
    icon: Cloud,
    features: [
      'Sincronización automática con Holded',
      'Gestión centralizada de clientes y proveedores',
      'Acceso a todas las funcionalidades de Holded',
      'Requiere cuenta activa en Holded'
    ],
    recommended: false,
    badge: 'Para usuarios de Holded'
  },
  {
    id: 'native',
    title: 'Contabilidad Nativa Zentro',
    description: 'Usa los módulos integrados de Zentro para gestionar ventas, facturas y gastos sin necesidad de software externo.',
    icon: Home,
    features: [
      'Módulo de ventas y facturación integrado',
      'Generación automática de PDFs',
      'Registro y categorización de gastos',
      'Exportación a Excel/CSV',
      'Sin coste adicional de software'
    ],
    recommended: true,
    badge: 'Recomendado'
  },
  {
    id: 'odoo',
    title: 'Integracion con Odoo',
    description: 'Opera sobre tu Odoo actual manteniendo el flujo estructurado de Zentro.',
    icon: Briefcase,
    features: [
      'Mantienes tu ERP Odoo actual',
      'Entrada estructurada por plantillas',
      'Sincronizacion y export operativo',
      'Trazabilidad completa del cierre mensual'
    ],
    recommended: false,
    badge: 'Para usuarios de Odoo'
  }
];

const FieldHelp = ({ text }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <HelpCircle className="w-4 h-4 text-slate-400 cursor-help" />
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p>{text}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export default function AccountingModeStep({ initialData, onNext, onBack, saving }) {
  const [selectedMode, setSelectedMode] = useState(initialData?.accounting_mode || null);

  const handleSubmit = () => {
    if (selectedMode) {
      onNext({ accounting_mode: selectedMode });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-1 flex items-center gap-2">
          Modo de Contabilidad
          <FieldHelp text="Elige cómo quieres gestionar tu facturación y gastos. Esta elección se puede cambiar más adelante contactando a soporte." />
        </h2>
        <p className="text-slate-600">
          Selecciona cómo quieres gestionar tu facturación y gastos en Zentro
        </p>
      </div>

      {/* Opciones de modo */}
      <div className="grid grid-cols-1 gap-4">
        {ACCOUNTING_MODES.map((mode) => {
          const Icon = mode.icon;
          const isSelected = selectedMode === mode.id;
          
          return (
            <div
              key={mode.id}
              onClick={() => setSelectedMode(mode.id)}
              className={`
                relative cursor-pointer rounded-xl border-2 p-6 transition-all duration-200
                ${isSelected 
                  ? 'border-primary bg-primary/5 ring-2 ring-primary ring-offset-2' 
                  : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}
              `}
              data-testid={`accounting-mode-${mode.id}`}
            >
              {/* Badge */}
              {mode.badge && (
                <div className={`
                  absolute -top-3 left-4 px-3 py-1 rounded-full text-xs font-semibold
                  ${mode.recommended 
                    ? 'bg-green-500 text-white' 
                    : 'bg-blue-100 text-blue-700'}
                `}>
                  {mode.badge}
                </div>
              )}
              
              {/* Selection indicator */}
              {isSelected && (
                <div className="absolute top-4 right-4 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}

              <div className="flex items-start gap-4">
                <div className={`
                  w-14 h-14 rounded-xl flex items-center justify-center shrink-0
                  ${isSelected ? 'bg-primary text-white' : 'bg-slate-100 text-slate-500'}
                `}>
                  <Icon className="w-7 h-7" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <h3 className={`text-lg font-semibold mb-1 ${isSelected ? 'text-primary' : 'text-slate-900'}`}>
                    {mode.title}
                  </h3>
                  <p className="text-sm text-slate-600 mb-4">
                    {mode.description}
                  </p>
                  
                  <ul className="space-y-2">
                    {mode.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <Check className={`w-4 h-4 shrink-0 ${isSelected ? 'text-primary' : 'text-green-500'}`} />
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Info adicional */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-sm text-blue-800">
          <strong>Nota:</strong> Si eliges "Contabilidad Nativa", podrás gestionar facturas y gastos directamente 
          desde Zentro sin necesidad de cuenta en Holded. Todos los datos se almacenarán de forma segura y 
          podrás exportarlos en cualquier momento.
        </p>
      </div>

      <div className="flex justify-between pt-6 border-t">
        <Button variant="outline" onClick={onBack} data-testid="btn-back">
          <ChevronLeft className="w-4 h-4 mr-2" /> Atrás
        </Button>
        <Button 
          onClick={handleSubmit} 
          disabled={!selectedMode || saving}
          data-testid="btn-next"
        >
          {saving ? 'Guardando...' : 'Continuar'} <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

